import { useState, useEffect, useMemo, useCallback } from "react";
import { Search, X, FolderOpen, BookOpen, CircleDot, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const SearchDialog = ({ open, onClose, syllabus, onNavigate }) => {
  const [query, setQuery] = useState("");

  // Flatten syllabus into searchable items
  const searchIndex = useMemo(() => {
    const items = [];
    const organSections = (syllabus || []).filter(n => n.title !== 'SLIDE');
    organSections.forEach((organ, organIdx) => {
      items.push({
        id: organ.id,
        title: organ.title,
        type: 'organ',
        organId: organ.id,
        organIdx,
        path: `Organ ${organIdx + 1}`,
        notes: '',
      });
      (organ.children || []).forEach((topic, topicIdx) => {
        items.push({
          id: topic.id,
          title: topic.title,
          type: 'topic',
          organId: organ.id,
          organIdx,
          topicIdx,
          path: `Organ ${organIdx + 1} > ${topic.title}`,
          notes: topic.notes || '',
        });
        (topic.children || []).forEach((point) => {
          items.push({
            id: point.id,
            title: point.title,
            type: 'point',
            organId: organ.id,
            organIdx,
            topicIdx,
            topicTitle: topic.title,
            path: `Organ ${organIdx + 1} > ${topic.title}`,
            notes: point.notes || '',
          });
        });
      });
    });
    return items;
  }, [syllabus]);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return searchIndex
      .filter(item =>
        item.title.toLowerCase().includes(q) ||
        item.notes.toLowerCase().includes(q)
      )
      .slice(0, 15);
  }, [query, searchIndex]);

  const handleSelect = useCallback((item) => {
    onNavigate(item.organId, item.topicIdx, item.id);
    onClose();
    setQuery("");
  }, [onNavigate, onClose]);

  // Keyboard shortcut
  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (open) onClose();
        else onClose(); // toggle via parent
      }
      if (e.key === 'Escape' && open) {
        onClose();
        setQuery("");
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const getIcon = (type) => {
    if (type === 'organ') return <FolderOpen size={16} className="text-amber-500" />;
    if (type === 'topic') return <BookOpen size={16} className="text-indigo-500" />;
    return <CircleDot size={14} className="text-blue-400" />;
  };

  const getTypeLabel = (type) => {
    if (type === 'organ') return 'Organ';
    if (type === 'topic') return 'Sub-Topik';
    return 'Point';
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[200]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => { onClose(); setQuery(""); }}
            data-testid="search-overlay"
          />
          <motion.div
            className="fixed top-[15%] left-1/2 w-[95%] max-w-[560px] z-[201] -translate-x-1/2"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden" data-testid="search-dialog">
              {/* Search Input */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
                <Search size={20} className="text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cari topik, sub-topik, atau point..."
                  className="flex-1 text-base outline-none bg-transparent text-gray-800 placeholder-gray-400"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  autoFocus
                  data-testid="search-input"
                />
                <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-400 bg-gray-100 rounded-md font-mono">ESC</kbd>
                <button onClick={() => { onClose(); setQuery(""); }} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400">
                  <X size={18} />
                </button>
              </div>

              {/* Results */}
              <div className="max-h-[360px] overflow-y-auto">
                {query.trim() && results.length === 0 && (
                  <div className="px-5 py-8 text-center">
                    <Search size={28} className="mx-auto text-gray-200 mb-2" />
                    <p className="text-sm text-gray-400">Tiada hasil untuk "{query}"</p>
                  </div>
                )}
                {results.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-blue-50 text-left group transition-colors duration-100"
                    data-testid="search-result-item"
                  >
                    <div className="flex-shrink-0">{getIcon(item.type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{item.title}</p>
                      <p className="text-xs text-gray-400 truncate">{item.path}</p>
                    </div>
                    <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                      {getTypeLabel(item.type)}
                    </span>
                    <ArrowRight size={14} className="text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-100 flex-shrink-0" />
                  </button>
                ))}
                {!query.trim() && (
                  <div className="px-5 py-6 text-center">
                    <p className="text-sm text-gray-400">Taip untuk mula mencari...</p>
                    <p className="text-xs text-gray-300 mt-1">Cari dalam semua 5 Organ, sub-topik & points</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-2.5 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono">Enter</kbd> pilih</span>
                <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono">Esc</kbd> tutup</span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
